import {
  RequestCaseEntityId, ReceiveCaseEntityId, FailRequestCaseEntityId,
  ReceiveConnectedCasePermissions, RequestConnectedCasePermissions,
  RequestChangeConnectionSequence, ReceiveChangeConnectionSequence, FailChangeConnectionSequence,
  ReceiveCase, FailLoadCase
} from './action-types';
import { api } from './api';
import { ActionDispatching, Entity, CasePermissions, ConstraintResponseType } from 'app/types-shared';
import { errorSafe } from 'app/infrastructure/redux';
import { tableName } from 'app/area/case/case-data-model';
import { getEntityContext, getCaseData } from 'app/infrastructure/ui/renderer/resolvers';
import { getChildEntitiesFromPath } from 'app/area/case/features/resolvers';
import { deepEquals } from 'app/infrastructure/js';
import { updateSequence } from 'app/area/case/features/connected-cases/sequence';
import { getAllEntitiesAtPath, getDraftId } from 'app/infrastructure/store';
import { CaseState } from 'app/area/case/types-store';
import { caseTypeId as getCaseTypeId, caseContextId as getCaseContextId } from 'app/infrastructure/data/wireformat';
import { actionCreators as responseCachingActionCreators } from 'app/area/shared/response-caching/actions';
import { ErrorNotification } from 'app/infrastructure/notifications/user-messages';
import { ReceiveDraftDocument } from 'app/area/case/draft/action-types';
import { caseDataToStore, caseMetadataToStore } from 'app/infrastructure/store/normalize';


const handleChangeSequenceError = (dispatch: any, err: any, connectedCaseId: number, allowWarnings: boolean) => {
  const errors = err?.validationErrors?.filter(error => error.responseType === ConstraintResponseType.Error);
  const warnings = err?.validationErrors?.filter(error => error.responseType === ConstraintResponseType.Warning);

  if (errors?.length) {
    dispatch(new ErrorNotification(errors.map(error => ({
      message: error.message,
      id: error.domain
    }))));
  } else if (warnings?.length && !allowWarnings) {
    dispatch(new FailChangeConnectionSequence(connectedCaseId, warnings.map(error => ({
      responseType: error.responseType,
      responseText: error.message,
      domainText: error.domainText
    }))));
  } else if (err?.userMessages) {
    dispatch(new ErrorNotification(err.userMessages));
    dispatch(new FailChangeConnectionSequence(connectedCaseId, err.userMessages));
  }
};

const handleChangeSequenceUpdate = async (dispatch: any, caseId: number, connectedCaseId: number) => {
  const caseResponse = await api.case(caseId);
  const caseData = caseResponse.json.data;
  if (!caseData) {
    dispatch(new FailLoadCase());
    return;
  }
  const caseType = getCaseTypeId(caseData);
  const caseContextId = getCaseContextId(caseData);
  const uiDef = await dispatch(responseCachingActionCreators.getUiDefinition(caseType, caseContextId));

  if (!uiDef) {
    dispatch(new FailLoadCase());
    return;
  }
  dispatch(new ReceiveCase(caseId, caseData, { ...uiDef, applicationId: caseType }));
  dispatch(new ReceiveChangeConnectionSequence(connectedCaseId));
};

const changeSequence = (appState: any, uiEngineProps: any, steps: number) => {

  if (steps === 0) {
    return [];
  }
  const resolveContext = getEntityContext(appState, uiEngineProps);
  const entities = getChildEntitiesFromPath(appState, uiEngineProps, 1, tableName.connectedCases);
  const currentIndex = entities.findIndex(c => deepEquals(c.id, resolveContext.path));
  const newSequence = updateSequence<Entity>(entities, currentIndex, steps);
  return newSequence;
};

export const getCurrentConnectedCases = (appState: any, uiEngineProps: any) => {
  const entities = getChildEntitiesFromPath(appState, uiEngineProps, 1, tableName.connectedCases);
  return entities.map(entity => entity.primaryKey.value);
};

export const connectedCaseActionCreators = {
  validateCaseId: (caseId: number): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {
      try {
        dispatch(new RequestCaseEntityId(caseId));
        const entityId = await api.caseEntityId(caseId);
        dispatch(new ReceiveCaseEntityId(caseId, entityId));
      } catch {
        dispatch(new FailRequestCaseEntityId(caseId));
      }
    },

  changeConnectionSequence: (uiEngineProps: any, steps: number, caseId?: number,
    allowWarnings?: boolean): ActionDispatching<any, any> =>
    errorSafe(async (dispatch: any, getState: any) => {
      const appState = getState();
      const isDraft = !caseId;
      const changed = changeSequence(appState, uiEngineProps, steps);
      const currentConnectedCases = getCurrentConnectedCases(appState, uiEngineProps);

      if (changed.length === 0) {
        return;
      }

      const connectedSequence = changed.find(c => deepEquals(c.id, uiEngineProps.resolveContext.path));
      const sequenceId = changed.findIndex(c => deepEquals(c.id, connectedSequence.id)) + 1;
      const connectedCaseId = uiEngineProps?.resolveContext?.primaryKey?.value;
      const connectionTypeId = connectedSequence?.id?.find(value => value?.TblConnectionTypes)?.TblConnectionTypes;
      const id = isDraft ? getDraftId(appState.caseArea.editing.values) : caseId;

      let caseResponse;
      let etag = null;
      if (!isDraft) {
        dispatch(new RequestChangeConnectionSequence(connectedCaseId));
        caseResponse = await api.case(caseId);
        etag = caseResponse.etag;
      } else {
        etag = appState.caseArea.editing.etag;
        dispatch(new RequestChangeConnectionSequence(connectedCaseId));
      }

      try {
        const response = await api.changeConnectionSequence(
          id,
          connectedCaseId,
          connectionTypeId,
          sequenceId,
          currentConnectedCases,
          allowWarnings,
          isDraft,
          etag
        );
        if (!isDraft) {
          await handleChangeSequenceUpdate(dispatch, caseId || id, connectedCaseId);
        } else {
          const { data, etag: responseEtag } = await response;
          const entities = caseDataToStore(data);
          dispatch(new ReceiveDraftDocument(id, entities, caseMetadataToStore(data), responseEtag)); 
          dispatch(new ReceiveChangeConnectionSequence(connectedCaseId));
        }
      } catch (err) {
        handleChangeSequenceError(dispatch, err, connectedCaseId, allowWarnings);
      }
    }),

  getPermissions: (uiEngineProps: any): ActionDispatching<any, any> =>
    errorSafe(async (dispatch: any, getState: any) => {
      const appState = getState();
      const caseState = appState.caseArea as CaseState;

      const connectedCaseIds = getAllEntitiesAtPath(
        getCaseData(caseState, uiEngineProps),
        [tableName.trans, tableName.connectionTypes, tableName.connectedCases]
      )
        .map(e => e.primaryKey.value);

      const ids = connectedCaseIds.filter(id =>
      (
        !caseState.stateForCaseIds ||
        !caseState.stateForCaseIds[id] ||
        (
          caseState.stateForCaseIds &&
          caseState.stateForCaseIds[id] &&
          !caseState.stateForCaseIds[id].fetchingPermissions &&
          !caseState.stateForCaseIds[id].permissions
        )
      )
      );

      if (!ids || ids.length === 0) {
        return;
      }

      dispatch(new RequestConnectedCasePermissions(ids));

      const [casePermissions, bowtieSupported] = await Promise.all([
        api.casePermissions(ids),
        api.bowtieSupported(ids)
      ]);

      const permissions = ids.reduce((result, id) => {
        const permission = casePermissions.find(p => p.caseId === id);
        const bowtie = bowtieSupported.find(p => p.caseId === id);
        return {
          ...result,
          [id]: {
            ...permission,
            bowtie: !!bowtie && bowtie.supported
          }
        };
      }, {} as { [id: number]: CasePermissions });

      dispatch(new ReceiveConnectedCasePermissions(permissions));
    })
};
